/**
 * WriterShoutoutsCard.jsx — public shoutouts a scribe publishes on their
 * profile (used to thank anonymous tippers, announce milestones, etc.).
 *
 * Modes
 *   • mine=true   → fetches /api/coins/writer/shoutouts and shows a
 *                   composer + delete buttons. Used by the scribe
 *                   themselves on their dashboard / profile.
 *   • mine=false  → fetches /api/coins/writer/{writer_id}/shoutouts
 *                   for the supplied writerId. Used on public profiles.
 *
 * Hides itself entirely when there are zero shoutouts in the public mode,
 * so it never shows an empty bracket on writer profiles that haven't
 * posted any.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Megaphone, Loader2, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_LEN = 240;

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const WriterShoutoutsCard = ({ mine = false, writerId = null }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);

  const url = mine
    ? `${BACKEND_URL}/api/coins/writer/shoutouts`
    : (writerId ? `${BACKEND_URL}/api/coins/writer/${writerId}/shoutouts` : null);

  const load = useCallback(async () => {
    if (!url) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await authFetch(url);
      if (!r.ok) return;
      const d = await r.json();
      setItems(d.shoutouts || []);
    } finally {
      setLoading(false);
    }
  }, [url]);

  useEffect(() => { load(); }, [load]);

  const post = async () => {
    const msg = text.trim();
    if (!msg) return;
    setPosting(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/coins/writer/shoutouts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setText('');
      toast.success('Shoutout posted.');
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to post shoutout');
    } finally {
      setPosting(false);
    }
  };

  const remove = async (id) => {
    setItems((prev) => prev.filter((s) => s.shoutout_id !== id));
    try {
      const r = await authFetch(`${BACKEND_URL}/api/coins/writer/shoutouts/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
    } catch (err) {
      toast.error('Failed to delete — refreshing.');
      await load();
    }
  };

  // Public mode: hide entirely if there are no shoutouts.
  if (!mine && !loading && items.length === 0) return null;

  return (
    <Card className="bg-slate-800/50 border-amber-500/30" data-testid="writer-shoutouts-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-white flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-amber-300" />
          Shoutouts
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {mine && (
          <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 space-y-2">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
              rows={2}
              placeholder="Thanks to everyone who keeps tipping anonymously — you keep me writing!"
              className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
              data-testid="writer-shoutouts-input"
            />
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-slate-400">{text.length}/{MAX_LEN} — public on your profile</span>
              <Button
                size="sm"
                onClick={post}
                disabled={posting || !text.trim()}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                data-testid="writer-shoutouts-post"
              >
                {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Post'}
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="py-3 text-center text-sm text-slate-400">
            <Loader2 className="w-4 h-4 inline-block animate-spin mr-1" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500 py-2">
            No shoutouts yet. Use the composer above to thank anonymous tippers.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="writer-shoutouts-list">
            {items.map((s) => (
              <li
                key={s.shoutout_id}
                className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 flex items-start gap-3"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-100 whitespace-pre-wrap break-words">{s.message}</p>
                  <p className="text-[11px] text-slate-500 mt-1">{formatTime(s.created_at)}</p>
                </div>
                {mine && (
                  <button
                    type="button"
                    onClick={() => remove(s.shoutout_id)}
                    className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-red-300"
                    aria-label="Delete shoutout"
                    data-testid={`writer-shoutouts-delete-${s.shoutout_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default WriterShoutoutsCard;
